/**
 * Desenho do Mapa da Produção em SVG — a "planta viva" vista de cima.
 *
 * Camadas (de baixo para cima): corredores do fluxo, salas (SalaSvg: piso,
 * cabeçalho, áreas, bancadas, pessoas) e os itens que andam (ItensSvg:
 * ambientes e peças desmembradas). O ponteado do chão é fundo CSS do palco,
 * deslocado junto com a vista (mais barato que um retângulo gigante com
 * padrão).
 *
 * Gestos (Pointer Events, como no ImagemViewer): um dedo/mouse arrasta a
 * vista, dois dedos dão zoom (pinça), a roda dá zoom no cursor. Um toque
 * (< 10 px) é um clique: no ambiente, na peça, na pessoa ou no "+N" abre o
 * popover; no convite abre a configuração; no resto da sala abre a página do
 * setor. A captura do ponteiro fica no <svg>, por isso o toque é detectado
 * aqui (no pointerup) e não por onClick nos elementos.
 *
 * Modo Organizar: arrastar uma sala (ou algo dentro dela) move a FORMA dela, a
 * alça do canto redimensiona, um toque seleciona e a alça do cabeçalho pede o
 * redesenho; o fundo continua arrastando a vista.
 *
 * DESENHO A MÃO LIVRE (v3): com `desenhando` ligado, o dedo/mouse risca no
 * mapa em vez de arrastar a vista. Os pontos são convertidos para coordenadas
 * do MUNDO na hora da captura (a correção do traçado tem tolerância em graus, e
 * em pixels de tela ela mudaria com o zoom) e, ao soltar, `corrigirTracado`
 * devolve uma forma reta/suave que sai em `onDesenhado`. Um segundo dedo no
 * meio do risco cancela o risco e vira pinça: no tablet o apoio da mão não pode
 * virar parede.
 *
 * Zoom semântico: o texto de dentro da sala é desenhado para a escala 1.
 * Com a sala menor na tela, em vez de sumir, ele é AMPLIADO na proporção
 * inversa (ampliacaoDoTexto) e continua com o mesmo tamanho aparente — é o
 * que deixa nomes, bancadas e itens legíveis com o mapa inteiro na tela.
 * Abaixo de LIMIAR_LEITURA nem ampliado caberia: aí ficam só as formas, as
 * cores e o cabeçalho da sala (que tem escala própria).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MotionConfig } from "framer-motion";
import { FiMaximize, FiMinus, FiPlus } from "react-icons/fi";

import { useThemeToggle } from "../../context/ThemeContext";
import { caixaAoPuxar, caixaEnvolvente, mover, paraPath, redimensionar } from "./dados/forma";
import { corrigirTracado } from "./dados/tracado";
import ItensSvg from "./ItensSvg";
import { caixaDosElementos } from "./layout";
import { pontoDeEntrada } from "./planta";
import SalaSvg, { AlcasDaSala } from "./SalaSvg";
import { BotaoControle, Controles, Palco } from "./styles";

const ESCALA_MIN = 0.2;
const ESCALA_MAX = 4;
const PASSO_BOTAO = 1.25;
const PASSO_RODA = 1.1;
const LIMIAR_TOQUE_PX = 10;
const MARGEM_AJUSTE_PX = 24;
// Texto de 12 unidades × (escala × k) ≥ ~10 px na tela: daqui para cima o
// texto sai no tamanho desenhado.
export const LIMIAR_DETALHE = 0.8;
// Abaixo disso o texto ampliado não caberia mais nas caixas: sem texto.
// 0,3 e não 0,38: num tablet de 1024 px a fábrica inteira cabe em escala
// ~0,39 e as salas menores caíam logo abaixo do limiar — a fila virava um
// campo de pontos sem nenhum número. Nesse degrau o rótulo do ambiente já cai
// para só o pedido (rotuloQueCabe) e os rótulos de bancada, área e pessoa
// se apagam (AMPLIACAO_MAX_*), então nada estoura a caixa.
export const LIMIAR_LEITURA = 0.3;
// Degraus da ampliação: o texto não é remontado a cada passo da roda.
const PASSO_AMPLIACAO = 0.05;
const PASSO_GRADE = 40;
const COMPRIMENTO_ENTRADA = 150;
// Espessura da tinta em pixels de TELA (dividida pela escala): um risco de
// dedo com 2,5 px quase não se vê no tablet enquanto a mão o cobre.
const LARGURA_TINTA_PX = 4;
// Abaixo disto (pixels de tela) o gesto foi um TOQUE, não um desenho: sem esse
// piso, um toque trêmulo abria o formulário de uma sala de 20 × 20.
const TRACO_MINIMO_PX = 24;

const limitar = (v, min, max) => Math.min(max, Math.max(min, v));
const coord = (valor) => Number(valor) || 0;

function zoomNoPonto(vista, fator, foco) {
  const escala = limitar(vista.escala * fator, ESCALA_MIN, ESCALA_MAX);
  const razao = escala / vista.escala;
  return {
    escala,
    tx: foco.x - razao * (foco.x - vista.tx),
    ty: foco.y - razao * (foco.y - vista.ty),
  };
}

// O que o ponteiro/teclado atingiu, do mais específico para o mais geral.
const ALVOS = [
  { tipo: "redesenhar", atributo: "data-redesenhar" },
  { tipo: "ambiente", atributo: "data-ambiente" },
  { tipo: "peca", atributo: "data-peca" },
  { tipo: "pessoa", atributo: "data-pessoa" },
  { tipo: "mais", atributo: "data-mais" },
  { tipo: "configurar", atributo: "data-configurar" },
  { tipo: "sala", atributo: "data-sala" },
];

export function identificarAlvo(elemento) {
  for (const { tipo, atributo } of ALVOS) {
    const encontrado = elemento?.closest?.(`[${atributo}]`);
    if (encontrado) {
      const sala = encontrado.closest?.("[data-sala]");
      return {
        tipo,
        id: encontrado.getAttribute(atributo),
        setor: encontrado.getAttribute("data-sala") || sala?.getAttribute("data-sala") || null,
        elemento: encontrado,
      };
    }
  }
  return null;
}

/**
 * Quanto o texto de uma sala precisa crescer para continuar do mesmo
 * tamanho na tela: 1 = tamanho desenhado, 0 = sem texto (longe demais).
 * `escalaNaTela` = escala da vista × k da sala.
 */
export function ampliacaoDoTexto(escalaNaTela) {
  if (!(escalaNaTela > 0)) return 0;
  if (escalaNaTela >= LIMIAR_DETALHE) return 1;
  if (escalaNaTela < LIMIAR_LEITURA) return 0;
  const degrau = Math.max(LIMIAR_LEITURA, Math.floor(escalaNaTela / PASSO_AMPLIACAO + 1e-9) * PASSO_AMPLIACAO);
  return LIMIAR_DETALHE / degrau;
}

// Objeto novo só quando algum setor muda de degrau: as salas e os itens são
// memo e não redesenham a cada passo de zoom.
function useAmpliacaoPorSetor(salas, escala) {
  const anteriorRef = useRef({});
  return useMemo(() => {
    const novo = Object.fromEntries(salas.map((s) => [s.setorId, ampliacaoDoTexto(escala * s.k)]));
    const antes = anteriorRef.current;
    const mesmo = Object.keys(novo).length === Object.keys(antes).length &&
      Object.entries(novo).every(([setorId, valor]) => antes[setorId] === valor);
    if (mesmo) return antes;
    anteriorRef.current = novo;
    return novo;
  }, [salas, escala]);
}

/**
 * O que a PLANTA desenha fora das paredes: a placa de cabeçalho (e a Fila ao
 * lado dela) da sala que o gestor encheu de subsetores. Entra no
 * enquadramento — só com as paredes, a placa ficava acima da borda da tela.
 */
export function extrasForaDasSalas(planta) {
  return (planta?.salas || []).flatMap((sala) =>
    sala.cabecalhoFora ? [sala.cabecalho, ...sala.areas.filter((a) => a.tipo === "fila")] : []
  );
}

function somarCaixas(caixa, extras) {
  const validas = extras.filter((e) => e && e.largura > 0 && e.altura > 0);
  if (!validas.length || !caixa.largura) return caixa;
  const x = Math.min(caixa.x, ...validas.map((e) => e.x));
  const y = Math.min(caixa.y, ...validas.map((e) => e.y));
  const direita = Math.max(caixa.x + caixa.largura, ...validas.map((e) => e.x + e.largura));
  const baixo = Math.max(caixa.y + caixa.altura, ...validas.map((e) => e.y + e.altura));
  return { x, y, largura: direita - x, altura: baixo - y };
}

function useVista(svgRef, elementos, chaveAjuste, extras) {
  const [vista, setVista] = useState({ escala: 1, tx: 0, ty: 0 });
  // A ref anda junto com cada mudança (não só depois do render): dois
  // eventos no mesmo quadro (roda, pinça) somam em vez de se perder.
  const vistaRef = useRef(vista);
  const elementosRef = useRef(elementos);
  useEffect(() => { elementosRef.current = elementos; }, [elementos]);
  const extrasRef = useRef(extras);
  useEffect(() => { extrasRef.current = extras; }, [extras]);
  // Enquanto a pessoa não mexeu na vista, o mapa se reenquadra sozinho
  // quando o palco muda de tamanho (janela, sidebar recolhida).
  const mexeuRef = useRef(false);

  const aplicar = useCallback((proxima) => {
    vistaRef.current = proxima;
    setVista(proxima);
  }, []);

  const ajustar = useCallback(() => {
    mexeuRef.current = false;
    const r = svgRef.current?.getBoundingClientRect();
    const caixa = somarCaixas(caixaDosElementos(elementosRef.current), extrasRef.current || []);
    if (!r?.width || !r?.height || !caixa.largura || !caixa.altura) {
      aplicar({ escala: 1, tx: 0, ty: 0 });
      return;
    }
    // A entrada da fábrica entra no enquadramento: cortada pela metade, ela
    // parecia defeito em vez de começo do fluxo.
    const entrada = pontoDeEntrada(elementosRef.current);
    const esquerda = Math.min(caixa.x, entrada.x);
    caixa.largura += caixa.x - esquerda;
    caixa.x = esquerda;
    const escala = limitar(
      Math.min((r.width - 2 * MARGEM_AJUSTE_PX) / caixa.largura, (r.height - 2 * MARGEM_AJUSTE_PX) / caixa.altura),
      ESCALA_MIN,
      ESCALA_MAX
    );
    aplicar({
      escala,
      tx: (r.width - caixa.largura * escala) / 2 - caixa.x * escala,
      ty: (r.height - caixa.altura * escala) / 2 - caixa.y * escala,
    });
  }, [svgRef, aplicar]);

  useLayoutEffect(() => { ajustar(); }, [ajustar, chaveAjuste]);
  // A placa só existe depois que o estado do mapa chega: reenquadra quando
  // ela aparece (ou some), desde que a pessoa ainda não tenha mexido na vista.
  const assinaturaExtras = (extras || []).map((e) => `${Math.round(e.x)},${Math.round(e.y)}`).join("|");
  useLayoutEffect(() => {
    if (!mexeuRef.current) ajustar();
  }, [ajustar, assinaturaExtras]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || typeof ResizeObserver === "undefined") return undefined;
    const observador = new ResizeObserver(() => {
      if (!mexeuRef.current) ajustar();
    });
    observador.observe(svg);
    return () => observador.disconnect();
  }, [svgRef, ajustar]);

  const mudarVista = useCallback((proxima) => {
    mexeuRef.current = true;
    aplicar(proxima);
  }, [aplicar]);

  return { vista, vistaRef, elementosRef, ajustar, mudarVista };
}

/**
 * Um corredor do fluxo: paredes, piso e a linha pontilhada com a seta do
 * sentido, os três no mesmo traçado. O piso do corredor passa por baixo das
 * salas — o vão da porta (`.porta`) é o que abre a parede na chegada.
 */
function Corredor({ x1, y1, x2, y2 }) {
  return (
    <g className="corredor-grupo">
      <line className="corredor-parede" x1={x1} y1={y1} x2={x2} y2={y2} />
      <line className="corredor-chao" x1={x1} y1={y1} x2={x2} y2={y2} />
      <line className="corredor" x1={x1} y1={y1} x2={x2} y2={y2} markerEnd="url(#mapa-producao-seta)" />
    </g>
  );
}

/**
 * Traço cru do dedo, enquanto o gesto não acabou (a correção é no soltar). O
 * traço é preenchido (CSS) enquanto é riscado: dá para ver a ÁREA que vai
 * virar sala, não só a linha.
 */
function Tinta({ pontos, escala }) {
  if (!pontos || pontos.length === 0) return null;
  const d = pontos.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  return <path className="tinta" d={d} strokeWidth={LARGURA_TINTA_PX / (escala || 1)} aria-hidden="true" />;
}

/**
 * Contorno já CORRIGIDO da sala que está sendo batizada: fica no mapa
 * enquanto o formulário pede nome e etapa. É o que mostra o que o traço
 * torto virou — sem isto a tinta sumia e nada aparecia no lugar até salvar.
 */
function Previa({ forma }) {
  const d = forma ? paraPath(forma) : "";
  if (!d) return null;
  return (
    <g className="previa" aria-hidden="true">
      <path className="previa-area" d={d} />
      <path className="previa-contorno" d={d} />
    </g>
  );
}

/**
 * Props de dados (todas de useCenaDoMapa): `planta` (montarPlanta), `resumos`
 * Map<setorId, resumoDaSala>, `gargalo` (id do setor ou null), `destaque`
 * (montarDestaque), `salasApagadas` Set, `situacoes`, `tarefasPorPosto`,
 * `pulsos`. `pronto=false` (estado do app ainda não chegou) desenha as salas
 * sem os itens, para eles não "voarem" da fila para as bancadas na abertura.
 *
 * Props do Organizar: `organizando`, `setorSelecionado`, `desenhando`
 * (`{setorId}` redesenha aquela sala; `{}` cria uma nova), `previa` (contorno
 * já corrigido da sala que está sendo batizada) e os avisos `onSelecionar`,
 * `onRedesenhar`, `onDesenhado(forma)`, `onFormaDoSetor(setorId, forma)` e
 * `onIniciarMudanca()` (um passo de Desfazer por gesto, no começo dele).
 */
export default function MapaSvg({
  elementos,
  planta,
  resumos,
  gargalo,
  destaque,
  salasApagadas,
  situacoes,
  tarefasPorPosto,
  pulsos,
  pronto = true,
  organizando = false,
  setorSelecionado = null,
  desenhando = null,
  previa = null,
  podeConfigurar = false,
  chaveAjuste,
  onAbrirSetor,
  onTocar,
  onConfigurar,
  onGesto,
  onFormaDoSetor,
  onIniciarMudanca,
  onSelecionar,
  onRedesenhar,
  onDesenhado,
  children,
}) {
  const svgRef = useRef(null);
  const ponteirosRef = useRef(new Map());
  const gestoRef = useRef(null);
  const [tracado, setTracado] = useState(null);
  const extras = useMemo(() => extrasForaDasSalas(planta), [planta]);
  const { vista, vistaRef, elementosRef, ajustar, mudarVista } = useVista(svgRef, elementos, chaveAjuste, extras);
  const { animacoes = true } = useThemeToggle() || {};
  const ampliacaoPorSetor = useAmpliacaoPorSetor(planta.salas, vista.escala);
  const nomePorSetor = useMemo(
    () => Object.fromEntries(planta.salas.map((s) => [s.setorId, s.nome])),
    [planta.salas]
  );
  const desenhandoRef = useRef(desenhando);
  useEffect(() => {
    desenhandoRef.current = desenhando;
    if (!desenhando) setTracado(null);
  }, [desenhando]);

  // Posições da leitura anterior: é de lá que um item "novo" sai andando.
  const origensRef = useRef(new Map());
  const primeiraCargaRef = useRef(true);
  useEffect(() => {
    origensRef.current = planta.posicoes;
    if (pronto) primeiraCargaRef.current = false;
  }, [planta.posicoes, pronto]);

  const pontoNoSvg = useCallback((clientX, clientY) => {
    const r = svgRef.current?.getBoundingClientRect();
    return { x: coord(clientX) - (r?.left || 0), y: coord(clientY) - (r?.top || 0) };
  }, []);

  // Coordenada do MUNDO (a mesma das formas): é nela que o traço é corrigido.
  const pontoNoMundo = useCallback((clientX, clientY) => {
    const tela = pontoNoSvg(clientX, clientY);
    const { escala, tx, ty } = vistaRef.current;
    return { x: (tela.x - tx) / (escala || 1), y: (tela.y - ty) / (escala || 1) };
  }, [pontoNoSvg, vistaRef]);

  const ancoraDe = useCallback((elemento) => {
    const r = svgRef.current?.getBoundingClientRect();
    const a = elemento?.getBoundingClientRect?.();
    if (!r || !a) return { x: 0, y: 0, largura: 0, altura: 0 };
    return { x: a.left - r.left, y: a.top - r.top, largura: a.width, altura: a.height };
  }, []);

  const disparar = useCallback((alvo) => {
    if (!alvo) {
      // Toque no vazio: fecha o popover (leitura) ou desfaz a seleção (Organizar).
      if (organizando) onSelecionar?.(null);
      else onTocar?.(null);
      return;
    }
    if (alvo.tipo === "redesenhar") {
      onRedesenhar?.(alvo.id);
      return;
    }
    if (organizando) {
      if (alvo.setor) onSelecionar?.(alvo.setor);
      return;
    }
    if (alvo.tipo === "sala") onAbrirSetor?.(alvo.setor);
    else if (alvo.tipo === "configurar") onConfigurar?.(alvo.id);
    else onTocar?.({ tipo: alvo.tipo, id: alvo.id, setor: alvo.setor, ancora: ancoraDe(alvo.elemento) });
  }, [organizando, onAbrirSetor, onConfigurar, onTocar, onSelecionar, onRedesenhar, ancoraDe]);

  // Roda do mouse: listener nativo não-passivo — o onWheel do React é
  // passivo e deixaria a página rolar junto com o zoom.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    const aoRolar = (e) => {
      e.preventDefault();
      onGesto?.();
      const foco = pontoNoSvg(e.clientX, e.clientY);
      const fator = e.deltaY < 0 ? PASSO_RODA : 1 / PASSO_RODA;
      mudarVista(zoomNoPonto(vistaRef.current, fator, foco));
    };
    svg.addEventListener("wheel", aoRolar, { passive: false });
    return () => svg.removeEventListener("wheel", aoRolar);
  }, [pontoNoSvg, mudarVista, vistaRef, onGesto]);

  const zoomPeloCentro = (fator) => {
    onGesto?.();
    const r = svgRef.current?.getBoundingClientRect();
    const foco = { x: (r?.width || 0) / 2, y: (r?.height || 0) / 2 };
    mudarVista(zoomNoPonto(vistaRef.current, fator, foco));
  };

  const meioDaPinca = (a, b) => pontoNoSvg((a.x + b.x) / 2, (a.y + b.y) / 2);

  const aoDescer = (e) => {
    // Botão direito/do meio do mouse não arrasta nem abre nada.
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const ponteiros = ponteirosRef.current;
    ponteiros.set(e.pointerId, { x: coord(e.clientX), y: coord(e.clientY) });
    e.currentTarget.setPointerCapture?.(e.pointerId);

    if (ponteiros.size >= 2) {
      const [a, b] = [...ponteiros.values()];
      // Segundo dedo no meio do risco: o risco é descartado e vira pinça.
      setTracado(null);
      gestoRef.current = { modo: "pinca", dist: Math.hypot(a.x - b.x, a.y - b.y), meio: meioDaPinca(a, b), deslocou: true };
      onGesto?.();
      return;
    }
    const base = { inicio: { x: coord(e.clientX), y: coord(e.clientY) }, vista: vistaRef.current, deslocou: false };
    if (desenhandoRef.current) {
      const ponto = pontoNoMundo(e.clientX, e.clientY);
      gestoRef.current = { ...base, modo: "desenhar", pontos: [ponto] };
      setTracado([ponto]);
      onGesto?.();
      return;
    }
    if (organizando) {
      const redesenhar = e.target.closest?.("[data-redesenhar]");
      if (redesenhar) {
        gestoRef.current = { ...base, modo: "pan", tocado: identificarAlvo(e.target) };
        return;
      }
      const alca = e.target.closest?.("[data-alca]");
      const sala = e.target.closest?.("[data-sala]");
      const setorId = alca?.getAttribute("data-alca") || sala?.getAttribute("data-sala") || null;
      const elemento = elementosRef.current.find((el) => el.setorId === setorId) || null;
      if (elemento) {
        // Um passo do Desfazer por GESTO: quem marca é o começo do arrasto, e
        // não cada `onFormaDoSetor` (são dezenas num arrasto só).
        onIniciarMudanca?.();
        gestoRef.current = {
          ...base,
          modo: alca ? "redimensionar" : "mover",
          // Alça sem lado (versão anterior do desenho) puxa pelo canto de baixo.
          lado: alca?.getAttribute("data-lado") || "se",
          setorId,
          forma: elemento.forma,
        };
        return;
      }
    }
    gestoRef.current = { ...base, modo: "pan", tocado: identificarAlvo(e.target) };
  };

  const moverPinca = (gesto, ponteiros) => {
    if (ponteiros.size < 2) return;
    const [a, b] = [...ponteiros.values()];
    const dist = Math.hypot(a.x - b.x, a.y - b.y);
    const meio = meioDaPinca(a, b);
    const fator = gesto.dist > 0 ? dist / gesto.dist : 1;
    const dx = gesto.meio ? meio.x - gesto.meio.x : 0;
    const dy = gesto.meio ? meio.y - gesto.meio.y : 0;
    const atual = vistaRef.current;
    mudarVista(zoomNoPonto({ ...atual, tx: atual.tx + dx, ty: atual.ty + dy }, fator, meio));
    gesto.dist = dist;
    gesto.meio = meio;
  };

  const aoMover = (e) => {
    const ponteiros = ponteirosRef.current;
    if (!ponteiros.has(e.pointerId)) return;
    ponteiros.set(e.pointerId, { x: coord(e.clientX), y: coord(e.clientY) });
    const gesto = gestoRef.current;
    if (!gesto) return;
    if (gesto.modo === "pinca") {
      moverPinca(gesto, ponteiros);
      return;
    }
    if (gesto.modo === "desenhar") {
      const ponto = pontoNoMundo(e.clientX, e.clientY);
      gesto.pontos.push(ponto);
      gesto.deslocou = true;
      setTracado([...gesto.pontos]);
      return;
    }
    if (!gesto.inicio) return;
    const dx = coord(e.clientX) - gesto.inicio.x;
    const dy = coord(e.clientY) - gesto.inicio.y;
    if (!gesto.deslocou && Math.hypot(dx, dy) >= LIMIAR_TOQUE_PX) {
      gesto.deslocou = true;
      onGesto?.();
    }
    if (gesto.modo === "pan") {
      if (gesto.deslocou) mudarVista({ ...gesto.vista, tx: gesto.vista.tx + dx, ty: gesto.vista.ty + dy });
      return;
    }
    const escala = gesto.vista.escala || 1;
    const alterada = gesto.modo === "mover"
      ? mover(gesto.forma, dx / escala, dy / escala)
      : redimensionar(gesto.forma, caixaAoPuxar(caixaEnvolvente(gesto.forma), gesto.lado, dx / escala, dy / escala));
    if (alterada) onFormaDoSetor?.(gesto.setorId, alterada);
  };

  const aoSoltar = (e) => {
    const ponteiros = ponteirosRef.current;
    ponteiros.delete(e.pointerId);
    const gesto = gestoRef.current;
    const soltou = e.type === "pointerup";
    if (gesto?.modo === "desenhar") {
      setTracado(null);
      if (soltou) {
        const forma = corrigirTracado(gesto.pontos, {
          extensaoMinima: TRACO_MINIMO_PX / (gesto.vista?.escala || 1),
        });
        if (forma) onDesenhado?.(forma);
      }
    } else if (gesto && soltou && !gesto.deslocou) {
      if (gesto.modo === "pan") disparar(gesto.tocado);
      else if (gesto.setorId) onSelecionar?.(gesto.setorId);
    }
    // Sobrou um dedo da pinça: não vira toque nem arrasto de repente.
    gestoRef.current = ponteiros.size > 0 ? { modo: "pinca", dist: 0, meio: null, deslocou: true } : null;
  };

  const aoTeclar = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    const alvo = identificarAlvo(e.target);
    if (!alvo) return;
    e.preventDefault();
    disparar(alvo);
  };

  const transformacao = `translate(${vista.tx} ${vista.ty}) scale(${vista.escala})`;
  const passo = PASSO_GRADE * vista.escala;
  const fundo = { backgroundSize: `${passo}px ${passo}px`, backgroundPosition: `${vista.tx}px ${vista.ty}px` };
  const { entrada } = planta;
  const temSala = planta.salas.length > 0;

  return (
    <Palco
      style={fundo}
      data-organizando={organizando ? "sim" : undefined}
      data-desenhando={desenhando ? "sim" : undefined}
    >
      <MotionConfig reducedMotion={animacoes ? "user" : "always"}>
        <svg
          ref={svgRef}
          role="group"
          aria-label="Mapa da Produção"
          onPointerDown={aoDescer}
          onPointerMove={aoMover}
          onPointerUp={aoSoltar}
          onPointerCancel={aoSoltar}
          onKeyDown={aoTeclar}
        >
          <defs>
            <pattern id="mapa-producao-piso" width="32" height="32" patternUnits="userSpaceOnUse">
              <path className="piso-junta" d="M32 0H0V32" />
            </pattern>
            {/* Luz de cima no tampo da bancada: as paradas pegam a cor do
                tema por CSS (stop-color), em vez de um gradiente por tema. */}
            <linearGradient id="mapa-producao-tampo" x1="0" y1="0" x2="0" y2="1">
              <stop className="tampo-alto" offset="0" />
              <stop className="tampo-baixo" offset="1" />
            </linearGradient>
            <marker
              id="mapa-producao-seta"
              viewBox="0 0 10 10"
              refX="7"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path className="corredor-seta" d="M1 1L8 5L1 9" />
            </marker>
          </defs>
          <g data-vista="sim" transform={transformacao}>
            {temSala && (
              <g className="corredores" aria-hidden="true">
                {planta.conectores.map((c) => (
                  <Corredor key={c.id} x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2} />
                ))}
                <Corredor
                  x1={entrada.x}
                  y1={entrada.y}
                  x2={entrada.x + COMPRIMENTO_ENTRADA - 20}
                  y2={entrada.y}
                />
                <text className="corredor-rotulo" x={entrada.x} y={entrada.y - 20} fontSize={14}>
                  Entrada
                </text>
              </g>
            )}
            {planta.salas.map((sala) => (
              <SalaSvg
                key={sala.setorId}
                sala={sala}
                resumo={resumos.get(sala.setorId)}
                gargalo={gargalo === sala.setorId}
                apagada={salasApagadas.has(sala.setorId)}
                ampliacao={ampliacaoPorSetor[sala.setorId] || 0}
                organizando={organizando}
                selecionada={setorSelecionado === sala.setorId}
                podeConfigurar={podeConfigurar}
                destaque={destaque}
                situacoes={situacoes}
                tarefasPorPosto={tarefasPorPosto}
                pulsos={pulsos}
              />
            ))}
            {/* As portas são desenhadas depois das salas: o vão só existe
                quando o piso cobre a parede. */}
            <g className="portas" aria-hidden="true">
              {planta.portas?.map((porta) => (
                <line key={porta.id} className="porta" x1={porta.x1} y1={porta.y1} x2={porta.x2} y2={porta.y2} />
              ))}
            </g>
            {pronto && (
              <ItensSvg
                ambientes={planta.ambientes}
                pecas={planta.pecas}
                mais={planta.mais}
                ampliacaoPorSetor={ampliacaoPorSetor}
                nomePorSetor={nomePorSetor}
                passa={destaque.passaAmbiente}
                passaPeca={destaque.passaPeca}
                pulsos={pulsos}
                organizando={organizando}
                origensRef={origensRef}
                entrada={entrada}
                primeiraCarga={primeiraCargaRef.current}
              />
            )}
            {/* Alças numa camada própria, ACIMA de salas e itens: numa sala em
                "L" o canto da caixa cai no recorte, e a vizinha cobria a alça. */}
            {organizando && (
              <g className="camada-alcas">
                {planta.salas.map((sala) => (
                  <AlcasDaSala
                    key={`alcas:${sala.setorId}`}
                    sala={sala}
                    escala={vista.escala}
                    selecionada={setorSelecionado === sala.setorId}
                  />
                ))}
              </g>
            )}
            <Previa forma={previa} />
            <Tinta pontos={tracado} escala={vista.escala} />
          </g>
        </svg>
      </MotionConfig>
      <Controles>
        <BotaoControle type="button" aria-label="Aproximar" onClick={() => zoomPeloCentro(PASSO_BOTAO)}>
          <FiPlus aria-hidden="true" />
        </BotaoControle>
        <BotaoControle type="button" aria-label="Afastar" onClick={() => zoomPeloCentro(1 / PASSO_BOTAO)}>
          <FiMinus aria-hidden="true" />
        </BotaoControle>
        <BotaoControle type="button" aria-label="Ajustar à tela" onClick={() => { onGesto?.(); ajustar(); }}>
          <FiMaximize aria-hidden="true" />
        </BotaoControle>
      </Controles>
      {children}
    </Palco>
  );
}
